import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trans, useTranslation } from 'react-i18next';
import type { MediaAsset, Playlist, PlaylistItem } from '@ghostscreen/contracts';
import { QueryEmptyState } from '../components/dashboard/QueryEmptyState';
import { QueryErrorState } from '../components/dashboard/QueryErrorState';
import { QueryLoadingBlock } from '../components/dashboard/QueryLoadingBlock';
import { apiErrorMessage } from '../i18n';
import { api } from '../lib/api';
import { useQueryRetry } from '../lib/dashboard/useQueryRetry';
import { useOrganisationId } from '../lib/session';
import { useDeepLinkParam } from '../lib/useDeepLinkParam';
import { getMediaOperatorStatus } from './content/mediaOperatorStatus';
import { buildPlaylistListRow, mergeCreatedPlaylist } from './playlists/buildPlaylistListModel';
import { PlaylistDrawer } from './playlists/PlaylistDrawer';
import { PlaylistList } from './playlists/PlaylistList';
import { useScreenPlaylistAssignments } from './playlists/useScreenPlaylistAssignments';

/**
 * Playlists page — list + right-hand edit drawer (Ghost Screen Playlists.dc.html).
 */
export function PlaylistsPage() {
  const { t, i18n } = useTranslation('playlists');
  const organisationId = useOrganisationId();
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [autoFocusName, setAutoFocusName] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const creatingFromHash = useRef(false);

  const openPlaylist = (playlistId: string, focusName = false) => {
    setSelectedId(playlistId);
    setAutoFocusName(focusName);
  };

  const closePlaylist = () => {
    setSelectedId(null);
    setAutoFocusName(false);
  };

  const playlistsQuery = useQuery({
    queryKey: ['playlists', organisationId],
    queryFn: () => api.listPlaylists(organisationId),
  });
  const mediaQuery = useQuery({
    queryKey: ['media', organisationId],
    queryFn: () => api.listMediaAssets(organisationId),
    refetchInterval: (query) => {
      const rows = query.state.data ?? [];
      return rows.some((asset) => getMediaOperatorStatus(asset).inProgress) ? 4_000 : false;
    },
  });
  const assignments = useScreenPlaylistAssignments(organisationId);
  const { isRetrying, retry } = useQueryRetry(() => playlistsQuery.refetch());

  const playlists = playlistsQuery.data ?? [];
  const mediaAssets = mediaQuery.data ?? [];

  useDeepLinkParam('playlistId', playlistsQuery.data, (playlist) => openPlaylist(playlist.id));

  const itemQueries = useQueries({
    queries: playlists.map((playlist) => ({
      queryKey: ['playlist-items', playlist.id],
      queryFn: () => api.listPlaylistItems(organisationId, playlist.id),
    })),
  });

  const mediaById = useMemo(
    () => new Map(mediaAssets.map((asset: MediaAsset) => [asset.id, asset])),
    [mediaAssets],
  );

  const rows = useMemo(() => {
    return playlists.map((playlist: Playlist, index) => {
      const items: PlaylistItem[] = itemQueries[index]?.data ?? [];
      return buildPlaylistListRow({
        playlist,
        items,
        mediaById,
        screenCount: assignments.screenCountByPlaylistId.get(playlist.id) ?? 0,
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- row labels are translated inside, so re-run on language change
  }, [playlists, itemQueries, mediaById, assignments.screenCountByPlaylistId, i18n.language]);

  const selected = playlists.find((playlist) => playlist.id === selectedId) ?? null;

  const create = useMutation({
    mutationFn: () => {
      const existing = new Set(playlists.map((playlist) => playlist.name));
      let name = t('page.newName');
      let i = 2;
      while (existing.has(name)) {
        name = t('page.newNameNumbered', { number: i });
        i += 1;
      }
      return api.createPlaylist(organisationId, { name });
    },
    onSuccess: (playlist) => {
      setCreateError(null);
      queryClient.setQueryData<Playlist[]>(['playlists', organisationId], (prev) =>
        mergeCreatedPlaylist(prev, playlist),
      );
      queryClient.setQueryData(['playlist-items', playlist.id], []);
      openPlaylist(playlist.id, true);
      void queryClient.invalidateQueries({ queryKey: ['playlists', organisationId] });
    },
    onError: (err) => {
      setCreateError(apiErrorMessage(err, t('page.createError')));
    },
  });

  useEffect(() => {
    if (location.hash !== '#new') {
      creatingFromHash.current = false;
      return;
    }
    if (creatingFromHash.current || create.isPending) return;
    creatingFromHash.current = true;
    navigate({ pathname: '/playlists', search: location.search }, { replace: true });
    create.mutate();
  }, [location.hash, location.search, navigate, create]);

  return (
    <div className="flex flex-col gap-[26px]">
      {createError ? (
        <p className="m-0 text-sm" style={{ color: 'var(--gs-bad)' }} role="alert">
          {createError}
        </p>
      ) : null}

      {playlistsQuery.isError || isRetrying ? (
        <QueryErrorState
          message={t('page.loadError')}
          onRetry={retry}
          isRetrying={isRetrying}
        />
      ) : playlistsQuery.isPending && playlists.length === 0 ? (
        <QueryLoadingBlock />
      ) : playlists.length === 0 ? (
        <QueryEmptyState>
          {create.isPending ? (
            t('page.creating')
          ) : (
            <Trans
              t={t}
              i18nKey="page.empty"
              components={{
                contentLink: <Link to="/content" className="font-semibold text-accent hover:text-signal" />,
              }}
            />
          )}
        </QueryEmptyState>
      ) : (
        <PlaylistList rows={rows} onOpen={(id) => openPlaylist(id)} />
      )}

      {selected ? (
        <PlaylistDrawer
          organisationId={organisationId}
          playlist={selected}
          mediaAssets={mediaAssets}
          screens={assignments.screens}
          assignmentByScreenId={assignments.assignmentByScreenId}
          autoFocusName={autoFocusName}
          onClose={closePlaylist}
          onDeleted={closePlaylist}
        />
      ) : null}
    </div>
  );
}
